import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { dataCache, CACHE_DURATIONS } from '../utils/dataCache';
import { isCloudflare503Error, isNetworkError } from '../utils/connectionUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { languageContent } from '../utils/languageContent';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import PullToRefresh from '../components/PullToRefresh';
import SyncButton from '../components/SyncButton';

interface Chapter {
  id: string;
  level_id: string;
  name: string;
  description: string;
  order_index: number;
  lesson_count?: number;
  completed_lessons?: number;
}

interface CourseLevel {
  id: string;
  level_code: string;
  level_name: string;
}

const ChaptersScreen: React.FC = () => {
  const { levelId } = useParams<{ levelId: string }>();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const content = languageContent[currentLanguage];
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [level, setLevel] = useState<CourseLevel | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (levelId) {
      // Load cached data immediately
      const cacheKey = `chapters_${levelId}`;
      const cachedChapters = dataCache.get<Chapter[]>(cacheKey);
      const cachedLevel = dataCache.get<CourseLevel>(`level_${levelId}`);
      
      if (cachedChapters) {
        setChapters(cachedChapters);
      }
      if (cachedLevel) {
        setLevel(cachedLevel);
      }
      
      // Load fresh data in background
      loadChapters(levelId);
      loadLevel(levelId);
    }
  }, [levelId]);

  const loadChapters = async (levelId: string, forceRefresh: boolean = false) => {
    try {
      const cacheKey = `chapters_${levelId}`;
      
      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedChapters = dataCache.get<Chapter[]>(cacheKey);
        if (cachedChapters) {
          setChapters(cachedChapters);
          // Refresh in background
          setTimeout(() => loadChapters(levelId, true), 100);
          return;
        }
      }
      
      // Get user progress from localStorage
      const progressKey = 'userProgress';
      const existingProgress = localStorage.getItem(progressKey);
      const progress = existingProgress ? JSON.parse(existingProgress) : {
        completedLessons: []
      };
      const completedLessons = progress.completedLessons || [];

      // Load chapters
      const { data: chaptersData, error: chaptersError } = await supabase
        .from('Chapters')
        .select('*')
        .eq('level_id', levelId)
        .order('order_index');

      if (chaptersError) {
        console.error('Error loading chapters:', chaptersError);
        
        // Check for Cloudflare 503
        if (isCloudflare503Error(chaptersError)) {
          console.warn('⚠️ Cloudflare is down (503). Keeping existing chapters.');
        } else if (isNetworkError(chaptersError)) {
          console.warn('⚠️ Network error. Keeping existing chapters.');
        }
        
        // Keep existing data on error
        if (chapters.length > 0) {
          return;
        }
        return;
      }

      // Single optimized query to get all lessons for all chapters
      const { data: allLessonsData } = await supabase
        .from('Lessons')
        .select('id, chapter_id')
        .in('chapter_id', (chaptersData || []).map(c => c.id));

      // Group lessons by chapter_id
      const lessonsByChapter: { [key: string]: any[] } = {};
      (allLessonsData || []).forEach(lesson => {
        if (!lessonsByChapter[lesson.chapter_id]) {
          lessonsByChapter[lesson.chapter_id] = [];
        }
        lessonsByChapter[lesson.chapter_id].push(lesson);
      });

      // Process chapters efficiently
      const chaptersWithCounts = (chaptersData || []).map(chapter => {
        const chapterLessons = lessonsByChapter[chapter.id] || [];
        const lessonCount = chapterLessons.length;
        
        // Calculate completed lessons in this chapter
        const chapterLessonIds = chapterLessons.map(l => l.id);
        const completedInChapter = chapterLessonIds.filter(id => completedLessons.includes(id)).length;
        
        return {
          ...chapter,
          lesson_count: lessonCount,
          completed_lessons: completedInChapter,
        };
      });

      // If refresh returns empty and we have existing data, preserve existing data
      if (chaptersWithCounts.length === 0 && chapters.length > 0) {
        console.warn('No chapters data returned from refresh, keeping existing data');
        return;
      }

      // Only update if we have data OR this is initial load (to show proper empty state)
      if (chaptersWithCounts.length === 0 && chapters.length === 0) {
        // Initial load with no data - show empty state
        setChapters([]);
        return;
      }

      setChapters(chaptersWithCounts);
      // Cache the data (only if we have data)
      if (chaptersWithCounts.length > 0) {
        dataCache.set(cacheKey, chaptersWithCounts, CACHE_DURATIONS.LEVELS);
      }
    } catch (error) {
      console.error('Error loading chapters:', error);
      // On error, keep existing data visible
    }
  };

  const loadLevel = async (levelId: string, forceRefresh: boolean = false) => {
    try {
      const cacheKey = `level_${levelId}`;
      
      if (!forceRefresh) {
        const cachedLevel = dataCache.get<CourseLevel>(cacheKey);
        if (cachedLevel) {
          setLevel(cachedLevel);
          return;
        }
      }
      
      const { data, error } = await supabase
        .from('CourseLevels')
        .select('*')
        .eq('id', levelId)
        .single();

      if (error) {
        console.error('Error loading level:', error);
      } else {
        setLevel(data);
        dataCache.set(cacheKey, data, CACHE_DURATIONS.LEVELS);
      }
    } catch (error) {
      console.error('Error loading level:', error);
    }
  };

  const handleChapterSelect = (chapterId: string) => {
    // Navigate directly to lessons
    navigate(`/chapter/${chapterId}/lessons`);
  };

  const handleRefresh = async () => {
    if (!levelId) return;
    setIsRefreshing(true);
    try {
      // Don't clear cache - just force refresh to get latest data
      // This way if refresh fails, we still have cached data visible
      await loadChapters(levelId, true);
      await loadLevel(levelId, true);
    } catch (error) {
      console.error('Error refreshing chapters:', error);
      // Keep existing data visible on error
    } finally {
      setIsRefreshing(false);
  }
  };

  return (
    <div className="chapters-screen">
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content.chapter}</h1>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <SyncButton 
            onSync={handleRefresh} 
            isSyncing={isRefreshing}
            size="small"
          />
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
        </div>
      </div>

      <LessonBannerAd />

      <PullToRefresh onRefresh={handleRefresh} disabled={isRefreshing}>
      <div className="level-info">
        <p>{level?.level_name}</p>
      </div>

      <div className="chapters-container">
        {chapters.map((chapter, index) => {
          const progressPercentage = chapter.lesson_count 
            ? Math.round(((chapter.completed_lessons || 0) / chapter.lesson_count) * 100)
            : 0;

          return (
            <div 
              key={chapter.id}
              className="chapter-card"
              onClick={() => handleChapterSelect(chapter.id)}
            >
              <div className="chapter-number">{index + 1}</div>
              
              <div className="chapter-content">
                <h3>{chapter.name}</h3>
                <p className="chapter-description">{chapter.description}</p>
                
                <div className="chapter-meta">
                  <span className="meta-item">📝 {chapter.lesson_count || 0} lessons</span>
                  {progressPercentage > 0 && (
                    <span className="meta-item">✅ {chapter.completed_lessons || 0}</span>
                  )}
                </div>
                
                <div className="chapter-progress-bar">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progressPercentage}%` }}></div>
                  </div>
                  <span className="progress-percentage">{progressPercentage}%</span>
                </div>
              </div>

              <div className="chapter-arrow">→</div>
            </div>
          );
        })}
      </div>
      </PullToRefresh>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ChaptersScreen;
